// Chemin: C:\gestion-copro\src\services\lot.service.ts
// Session 47 : Gestion de proprietaireId (source unique)
// Session 72 : Ajout getLotsByProprietaire pour espace copropriétaire
// Session 72 FIX : Correction logique - proprietaireId = ID copropriétaire, pas userId

import {
  collection,
  doc,
  getDocs,
  getDoc,
  addDoc,
  updateDoc,
  deleteDoc,
  deleteField,
  query,
  where,
  orderBy,
  Timestamp
} from 'firebase/firestore';
import { db } from './firebase';
import { Lot, LotCreateData, LotUpdateData, LotResume } from '../types/lot.types';

const COLLECTION_NAME = 'lots';

/**
 * Récupère tous les lots d'une copropriété
 */
export const getLotsByCopropriete = async (coproprieteId: string): Promise<Lot[]> => {
  const q = query(
    collection(db, COLLECTION_NAME),
    where('coproprieteId', '==', coproprieteId),
    orderBy('numero', 'asc')
  );
  
  const snapshot = await getDocs(q);
  return snapshot.docs.map(doc => ({
    id: doc.id,
    ...doc.data()
  } as Lot));
};

/**
 * Session 72 : Récupère les lots d'un copropriétaire par son userId Firebase
 * 
 * Logique :
 * 1. Trouver le copropriétaire dont userId === firebaseUserId
 * 2. Utiliser l'ID de ce copropriétaire pour chercher les lots (proprietaireId)
 * 3. Inclure les lots rattachés
 */
export const getLotsByProprietaire = async (
  coproprieteId: string,
  firebaseUserId: string
): Promise<Lot[]> => {
  // Étape 1 : Trouver le copropriétaire lié à cet utilisateur Firebase
  const coproprietairesRef = collection(db, 'coproprietaires');
  const qCopro = query(
    coproprietairesRef,
    where('coproprieteId', '==', coproprieteId),
    where('userId', '==', firebaseUserId)
  );
  
  const coproSnapshot = await getDocs(qCopro);
  
  if (coproSnapshot.empty) {
    // Pas de copropriétaire lié à cet utilisateur
    return [];
  }
  
  const coproprietaireId = coproSnapshot.docs[0].id;
  
  // Étape 2 : Récupérer les lots où proprietaireId === coproprietaireId
  const qLots = query(
    collection(db, COLLECTION_NAME),
    where('coproprieteId', '==', coproprieteId),
    where('proprietaireId', '==', coproprietaireId)
  );
  
  const lotsSnapshot = await getDocs(qLots);
  const lotsPrincipaux = lotsSnapshot.docs.map(doc => ({
    id: doc.id,
    ...doc.data()
  } as Lot));
  
  // Étape 3 : Pour chaque lot principal, récupérer les lots rattachés
  const lotsRattachesPromises = lotsPrincipaux
    .filter(lot => ['appartement', 'studio', 'local_commercial', 'bureau'].includes(lot.type))
    .map(lot => getLotsRattaches(lot.id));
  
  const lotsRattachesArrays = await Promise.all(lotsRattachesPromises);
  const lotsRattaches = lotsRattachesArrays.flat();
  
  // Combiner et trier par numéro
  const tousLesLots = [...lotsPrincipaux, ...lotsRattaches];
  return tousLesLots.sort((a, b) => {
    const numA = parseInt(a.numero.replace(/\D/g, '')) || 0;
    const numB = parseInt(b.numero.replace(/\D/g, '')) || 0;
    return numA - numB;
  });
};

/**
 * Récupère un lot par son ID
 */
export const getLotById = async (lotId: string): Promise<Lot | null> => {
  const docRef = doc(db, COLLECTION_NAME, lotId);
  const docSnap = await getDoc(docRef);
  
  if (!docSnap.exists()) {
    return null;
  }
  
  return {
    id: docSnap.id,
    ...docSnap.data()
  } as Lot;
};

/**
 * Crée un nouveau lot
 * Session 47 : Gestion de proprietaireId
 */
export const createLot = async (data: LotCreateData): Promise<string> => {
  const now = Timestamp.now();
  
  // Construire l'objet sans champs undefined
  const lotData: Record<string, any> = {
    coproprieteId: data.coproprieteId,
    numero: data.numero,
    type: data.type,
    tantièmesGeneraux: data.tantièmesGeneraux,
    proprietaire: {
      nom: data.proprietaire.nom
    },
    createdAt: now,
    updatedAt: now
  };
  
  // Session 47 : Ajouter proprietaireId si présent
  if (data.proprietaireId) {
    lotData.proprietaireId = data.proprietaireId;
  }
  
  // Champs optionnels du lot
  if (data.batiment) lotData.batiment = data.batiment;
  if (data.etage) lotData.etage = data.etage;
  if (data.description) lotData.description = data.description;
  if (data.lotPrincipalId) lotData.lotPrincipalId = data.lotPrincipalId;
  if (data.tantièmesSpeciaux) lotData.tantièmesSpeciaux = data.tantièmesSpeciaux;
  
  // Champs optionnels du propriétaire (rétrocompat)
  if (data.proprietaire.prenom) {
    lotData.proprietaire.prenom = data.proprietaire.prenom;
  }
  if (data.proprietaire.email) {
    lotData.proprietaire.email = data.proprietaire.email;
  }
  if (data.proprietaire.telephone) {
    lotData.proprietaire.telephone = data.proprietaire.telephone;
  }
  if (data.proprietaire.adresseCorrespondance) {
    lotData.proprietaire.adresseCorrespondance = data.proprietaire.adresseCorrespondance;
  }
  if (data.proprietaire.userId) {
    lotData.proprietaire.userId = data.proprietaire.userId;
  }
  
  const docRef = await addDoc(collection(db, COLLECTION_NAME), lotData);
  return docRef.id;
};

/**
 * Met à jour un lot existant
 * Session 47 : Gestion de proprietaireId (null pour délier)
 */
export const updateLot = async (lotId: string, data: LotUpdateData): Promise<void> => {
  const docRef = doc(db, COLLECTION_NAME, lotId);
  
  const updateData: Record<string, any> = {
    updatedAt: Timestamp.now()
  };
  
  // Champs simples
  if (data.numero !== undefined) updateData.numero = data.numero;
  if (data.type !== undefined) updateData.type = data.type;
  if (data.batiment !== undefined) updateData.batiment = data.batiment;
  if (data.etage !== undefined) updateData.etage = data.etage;
  if (data.description !== undefined) updateData.description = data.description;
  if (data.tantièmesGeneraux !== undefined) updateData.tantièmesGeneraux = data.tantièmesGeneraux;
  if (data.tantièmesSpeciaux !== undefined) updateData.tantièmesSpeciaux = data.tantièmesSpeciaux;
  
  // Gestion du rattachement parent/enfant
  // Si null => on détache (suppression du champ)
  // Si string => on rattache (ajout du champ)
  if (data.lotPrincipalId === null) {
    updateData.lotPrincipalId = deleteField();
  } else if (data.lotPrincipalId !== undefined) {
    updateData.lotPrincipalId = data.lotPrincipalId;
  }
  
  // Session 47 : Gestion de proprietaireId
  // Si null => on délie (suppression du champ)
  // Si string => on lie (ajout du champ)
  if (data.proprietaireId === null) {
    updateData.proprietaireId = deleteField();
  } else if (data.proprietaireId !== undefined) {
    updateData.proprietaireId = data.proprietaireId;
  }
  
  // Propriétaire (mise à jour partielle pour rétrocompat)
  if (data.proprietaire) {
    // Récupérer le lot actuel pour fusionner
    const currentLot = await getLotById(lotId);
    if (currentLot) {
      updateData.proprietaire = {
        ...currentLot.proprietaire,
        ...data.proprietaire
      };
      // Nettoyer les undefined
      Object.keys(updateData.proprietaire).forEach(key => {
        if (updateData.proprietaire[key] === undefined) {
          delete updateData.proprietaire[key];
        }
      });
    }
  }
  
  await updateDoc(docRef, updateData);
};

/**
 * Supprime un lot
 */
export const deleteLot = async (lotId: string): Promise<void> => {
  const docRef = doc(db, COLLECTION_NAME, lotId);
  await deleteDoc(docRef);
};

/**
 * Récupère les lots résumés (pour les selects/listes)
 */
export const getLotsResume = async (coproprieteId: string): Promise<LotResume[]> => {
  const lots = await getLotsByCopropriete(coproprieteId);
  
  return lots.map(lot => ({
    id: lot.id,
    numero: lot.numero,
    type: lot.type,
    proprietaireNom: lot.proprietaire.prenom 
      ? `${lot.proprietaire.prenom} ${lot.proprietaire.nom}`
      : lot.proprietaire.nom,
    tantièmesGeneraux: lot.tantièmesGeneraux
  }));
};

/**
 * Calcule le total des tantièmes généraux
 */
export const calculerTotalTantiemes = async (coproprieteId: string): Promise<number> => {
  const lots = await getLotsByCopropriete(coproprieteId);
  return lots.reduce((total, lot) => total + lot.tantièmesGeneraux, 0);
};

/**
 * Recherche un lot par son numéro
 */
export const getLotByNumero = async (
  coproprieteId: string, 
  numero: string
): Promise<Lot | null> => {
  const q = query(
    collection(db, COLLECTION_NAME),
    where('coproprieteId', '==', coproprieteId),
    where('numero', '==', numero)
  );
  
  const snapshot = await getDocs(q);
  
  if (snapshot.empty) {
    return null;
  }

  const docSnap = snapshot.docs[0];
  return {
    id: docSnap.id,
    ...docSnap.data()
  } as Lot;
};

/**
 * Récupère les lots rattachés à un lot principal
 */
export const getLotsRattaches = async (lotPrincipalId: string): Promise<Lot[]> => {
  const q = query(
    collection(db, COLLECTION_NAME),
    where('lotPrincipalId', '==', lotPrincipalId)
  );
  
  const snapshot = await getDocs(q);
  return snapshot.docs.map(doc => ({
    id: doc.id,
    ...doc.data()
  } as Lot));
};

/**
 * Détache tous les lots rattachés à un lot principal
 */
export const detacherTousLesLots = async (lotPrincipalId: string): Promise<number> => {
  const lotsRattaches = await getLotsRattaches(lotPrincipalId);
  
  for (const lot of lotsRattaches) {
    await updateLot(lot.id, { lotPrincipalId: null });
  }
  
  return lotsRattaches.length;
};
